import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import {
  BehaviorSubject,
  catchError,
  filter,
  finalize,
  map,
  NEVER,
  Observable,
  of,
  shareReplay,
  skipWhile,
  switchMap,
  tap,
} from 'rxjs';
import { Validators } from '@angular/forms';
import * as Sentry from '@sentry/angular';
import { User } from '../models/user';
import { StatusMessageResponse } from '../models/statusMessageResponse';
import { NimmstaCommonsModuleConfiguration } from '../nimmsta-commons-module.config';
import { CustomValidators } from '../utils/custom-validators';
import { KeycloakCredential } from '../models/keycloakCredential';
import { FeatureFlag } from '../models/featureFlags';
import Keycloak, { KeycloakTokenParsed } from 'keycloak-js';
import { UserPermissionsResponse } from '../models/user-permissions';
import {
  derivePrimaryRole,
  mergePermissionScopes,
  normalizeUserRoles,
} from '../utils/authorization.util';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly baseUrl;

  private http = inject(HttpClient);
  private keycloak = inject(Keycloak);
  private configuration = inject(NimmstaCommonsModuleConfiguration);

  private currentUserSubject: BehaviorSubject<User | null>;

  private currentUser: Observable<User | null>;

  private currentUserLoad$?: Observable<User>;

  public static pwdValidators = [
    Validators.minLength(8),
    Validators.maxLength(255),
    CustomValidators.patternValidator(/\d/, { hasNumber: true }),
    CustomValidators.patternValidator(/[A-Z]/, { hasCapitalCase: true }),
    CustomValidators.patternValidator(/[a-z]/, { hasSmallCase: true }),
    CustomValidators.patternValidator(/[!@#$%^&*(),.?":{}|<>';]/, {
      hasSpecialCharacters: true,
    }),
  ];

  CURRENT_ORG_ID_KEY = 'currentOrgIdForUser-';

  constructor() {
    if (!this.configuration.apiUrls.workflow) {
      throw new Error('No workflow API URL configured');
    }

    this.baseUrl = `${this.configuration.apiUrls.workflow!}/api/v1/auth`;
    this.currentUserSubject = new BehaviorSubject<User | null>(null);
    this.currentUser = this.currentUserSubject.asObservable();

    // Subscribe to keycloak events after ensuring keycloak is initialized
    this.setupKeycloakEventHandlers();
  }

  private setupKeycloakEventHandlers(): void {
    this.keycloak.onAuthRefreshError = () => {
      this.logout(window.location.href);
    };

    this.keycloak.onAuthError = () => {
      this.currentUserSubject.next(null);
    };

    this.keycloak.onAuthLogout = () => {
      this.currentUserSubject.next(null);
    };
  }

  async logout(returnUrl?: string): Promise<void> {
    localStorage.removeItem(this.configuration.USER_KEY);
    sessionStorage.clear();
    Sentry.setUser(null);
    return this.keycloak.logout({
      redirectUri: returnUrl || window.location.origin,
    });
  }

  public getCurrentUser(): Observable<User> {
    return this.currentUser.pipe(
      skipWhile((user) => user == null), // wait until user is not null
      map((user) => user as User), // skipWhile ensures that user is not null
    );
  }

  public getCurrentUserValue(): User | null {
    return this.currentUserSubject.value;
  }

  public ensureCurrentUser(): Observable<User> {
    return this.currentUserSubject.value
      ? of(this.currentUserSubject.value)
      : this.loadCurrentUser();
  }

  public getIdToken(): KeycloakTokenParsed | undefined {
    return this.keycloak.idTokenParsed;
  }

  public loadCurrentUser(): Observable<User> {
    // Check if keycloak is initialized and the user is authenticated
    if (!this.keycloak.didInitialize || !this.keycloak.authenticated) {
      return NEVER;
    }

    if (this.currentUserLoad$) {
      return this.currentUserLoad$;
    }

    this.currentUserLoad$ = this.http
      .get<User>(`${this.configuration.apiUrls.workflow!}/api/v1/user`)
      .pipe(
        switchMap((user) =>
          this.loadCurrentUserPermissions().pipe(
            map((permissions) => this.normalizeCurrentUser(user, permissions)),
          ),
        ),
        tap((user) => {
          const actingOrgId = this.getActingOrganizationId();
          if (actingOrgId && actingOrgId !== user.organization.id) {
            user.currentOrganization =
              user.secondaryOrganizations.find(
                (org) => org.id === actingOrgId,
              ) ?? user.organization;
          } else {
            user.currentOrganization = user.organization;
          }
          localStorage.setItem(
            this.configuration.USER_KEY,
            JSON.stringify(user),
          );
          this.currentUserSubject.next(user);
          this.initializeSentryContext(user, actingOrgId);
        }),
        finalize(() => {
          this.currentUserLoad$ = undefined;
        }),
        shareReplay(1),
      );

    return this.currentUserLoad$;
  }

  setActingOrganization(orgId: string): void {
    sessionStorage.setItem(
      this.CURRENT_ORG_ID_KEY + this.currentUserSubject.value!.id,
      orgId,
    );
    const user = this.currentUserSubject.value!;
    if (orgId !== user.organization.id) {
      const org = user.secondaryOrganizations.find((org) => org.id === orgId);
      if (!org) {
        throw new Error(
          `User is not a member of the organization with id ${orgId}`,
        );
      }
      user.currentOrganization = org;
    } else {
      user.currentOrganization = user.organization;
    }
  }

  getActingOrganizationId(): string | null {
    if (this.keycloak.idTokenParsed) {
      return (
        sessionStorage.getItem(
          this.CURRENT_ORG_ID_KEY + this.keycloak.idTokenParsed['userId'],
        ) ||
        this.keycloak.idTokenParsed['homeOrgId'] ||
        null
      );
    }

    return null;
  }

  initializeSentryContext(user: User, actingOrgId: string | null): void {
    Sentry.setUser({
      id: user?.id,
      email: user?.email,
      username: `${user?.given_name} ${user?.family_name}`,
      firstName: user?.given_name,
      lastName: user?.family_name,
      homeOrganization: `${user?.organization?.name ?? 'unknown name'} (${user?.organization?.id ?? 'unknown id'})`,
      actingOrganization: actingOrgId
        ? `${[user?.organization, ...(user?.secondaryOrganizations ?? [])].find((org) => org?.id === actingOrgId)?.name ?? 'unknown name'} (${actingOrgId})`
        : null,
      role: user?.role,
      ip_address: '{{auto}}',
    });
  }

  forgotPassword(email: string): Observable<void> {
    let params = new HttpParams();
    params = params.append('email', email);
    return this.http.get<void>(`${this.baseUrl}/forgot-password`, { params });
  }

  resetPassword(
    code: string,
    username: string,
    password: string,
  ): Observable<void> {
    const body = {
      code,
      username,
      newPassword: password,
    };
    return this.http.put<void>(`${this.baseUrl}/reset-password`, body);
  }

  add2FADevice(): Promise<void> {
    return this.keycloak.login({ action: 'CONFIGURE_TOTP' });
  }

  removeKeycloakCredential(id: string): Promise<void> {
    if (!id) {
      throw new Error('No id provided');
    }

    return this.keycloak.login({ action: `delete_credential:${id}` });
  }

  updatePassword(): Promise<void> {
    return this.keycloak.login({ action: 'UPDATE_PASSWORD' });
  }

  updateProfile(): Promise<void> {
    return this.keycloak.login({ action: 'UPDATE_PROFILE' });
  }

  registerWebauthnPasswordless(): Promise<void> {
    return this.keycloak.login({ action: 'webauthn-register-passwordless' });
  }

  getCredentials(): Observable<KeycloakCredential[]> {
    return this.http.get<KeycloakCredential[]>(
      `${this.configuration.keycloakUrl}/realms/${this.configuration.keycloakRealm}/account/credentials`,
    );
  }

  resendVerification(email: string): Observable<StatusMessageResponse> {
    return this.http.put<StatusMessageResponse>(
      `${this.configuration.apiUrls.workflow!}/api/v1/user/resend-verification-email`,
      {},
      { params: { email } },
    );
  }

  requestAccess(
    verificationCode: string,
    email: string,
    givenName: string,
    familyName: string,
    organizationName: string,
  ): Observable<StatusMessageResponse> {
    const body = {
      email,
      givenName,
      familyName,
      organizationName,
    };
    const params = new HttpParams().set('code', verificationCode);
    return this.http.post<StatusMessageResponse>(
      `${this.configuration.apiUrls.workflow!}/api/v1/user/access-request`,
      body,
      { params },
    );
  }

  impersonate(email: string): Observable<{
    refresh_token: string;
    access_token: string;
    id_token: string;
  }> {
    return of(this.keycloak.token).pipe(
      filter((token) => token !== undefined),
      map((token) => ({
        body: new HttpParams()
          .set('client_id', this.configuration.keycloakClientId)
          .set('grant_type', 'urn:ietf:params:oauth:grant-type:token-exchange')
          .set('subject_token', token)
          // urn:ietf:params:oauth:token-type:access_token does not include refresh_token
          .set(
            'requested_token_type',
            'urn:ietf:params:oauth:token-type:refresh_token',
          )
          .set('audience', this.configuration.keycloakClientId)
          .set('requested_subject', email),
        headers: new HttpHeaders({
          'Content-Type': 'application/x-www-form-urlencoded',
        }),
      })),
      switchMap(({ body, headers }) =>
        this.http.post<{
          refresh_token: string;
          access_token: string;
          id_token: string;
        }>(
          `${this.configuration.keycloakUrl}/realms/${this.configuration.keycloakRealm}/protocol/openid-connect/token`,
          body.toString(),
          { headers },
        ),
      ),
    );
  }

  private normalizeCurrentUser(
    user: User,
    permissions: UserPermissionsResponse,
  ): User {
    const roles = normalizeUserRoles(
      user.roles?.length ? user.roles : this.getTokenRoles(),
    );

    return {
      ...user,
      roles,
      role: derivePrimaryRole(roles),
      featureFlags: this.getFeatureFlags(),
      permissions: permissions.permissions ?? [],
      permissionScopes: mergePermissionScopes(permissions.scopes),
      hasAllPermissions: permissions.hasAllPermissions ?? false,
    };
  }

  private loadCurrentUserPermissions(): Observable<UserPermissionsResponse> {
    return this.http
      .get<UserPermissionsResponse>(
        `${this.configuration.apiUrls.workflow!}/api/v1/user/permissions`,
      )
      .pipe(
        catchError(() =>
          of({
            hasAllPermissions: false,
            permissions: [],
            scopes: [],
          }),
        ),
      );
  }

  private getTokenRoles(): string[] {
    return (
      this.keycloak.tokenParsed?.resource_access?.[
        this.configuration.keycloakClientId
      ]?.roles ?? []
    );
  }

  private getFeatureFlags(): FeatureFlag[] {
    return this.getTokenRoles()
      .filter((r) => Object.keys(FeatureFlag).includes(r.toUpperCase()))
      .map(
        (flag) => FeatureFlag[flag.toUpperCase() as keyof typeof FeatureFlag],
      );
  }

  private getOrganizationIds(user: User): string[] | undefined {
    // user.groups = [/[a-zA-Z0-9-], /ROLE_..]
    const regex = /^[a-zA-Z0-9-]+$/g;
    return user.group
      .filter((group) => group.split('/').length === 2)
      .map((group) => group.slice(1))
      .filter((group) => regex.test(group));
  }
}
